/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { Attributes } from '@leverege/ui-attributes';
import { ValueCache } from '@leverege/value-cache';
import { connect } from 'react-redux';
import I18N from '@leverege/i18n';
import BulkCreateMapFields from "./BulkCreateMapFields.js";
import BulkCreateUpload from "./BulkCreateUpload.js";
import { BlueprintAttributeFormValidatorFactory } from "./validators/Factory.js";
import FileUtil from "./file/Util.js";
import ParseUtil from "./file/ParseType.js";
import Util from "./Utils.js";
import { jsx as _jsx } from "react/jsx-runtime";
const {
  sort,
  defaultGetAttributes,
  bulkCreateAttributeFilter
} = Util;
const {
  t,
  tIcon
} = I18N.ns('molten.imagine.forms.BulkCreateUploadForm');
class BulkCreateUploadForm extends React.Component {
  static propTypes = {
    getAttributes: PropTypes.func,
    contentVariant: PropTypes.string,
    contentHeaderVariant: PropTypes.string,
    contentAreaVariant: PropTypes.string,
    contentFooterVariant: PropTypes.string,
    titleVariant: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    titleIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    subtitleVariant: PropTypes.string,
    bodyVariant: PropTypes.string,
    downloadVariant: PropTypes.string,
    downloadIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    eventData: PropTypes.any,
    submitText: PropTypes.string,
    cancelText: PropTypes.string,
    loading: PropTypes.bool
  };
  static defaultProps = {
    getAttributes: defaultGetAttributes,
    contentVariant: 'default',
    contentHeaderVariant: 'formHeader',
    contentAreaVariant: 'formBody',
    contentFooterVariant: 'formButtons',
    titleVariant: 'dialogTitle',
    get title() {
      return t('title');
    },
    get titleIcon() {
      return tIcon('titleIcon', 'fa fa-plus fa-fw');
    },
    subtitleVariant: 'dialogTitle',
    bodyVariant: 'dialogBody|body',
    downloadVariant: 'tertiary',
    get downloadIcon() {
      return tIcon('downloadIcon', 'fa fa-download');
    },
    eventData: null,
    get submitText() {
      return t('submitText');
    },
    get cancelText() {
      return t('cancelText');
    },
    loading: false
  };
  constructor(props) {
    super(props);
    this.cache = new ValueCache();
    this.state = {
      step: 'upload'
    };
  }
  onSubmit = evt => {
    const {
      onSubmit,
      eventData,
      target
    } = this.props;
    const {
      data,
      attrMap
    } = this.state;
    const newDevices = FileUtil.csvToJson(data);
    const attributes = this.getAttributes();
    const validator = this.buildValidator();
    const idAttrs = attributes.reduce((acc, curr) => {
      const externalId = curr.blueprint?.attribute?.getTagStartingWith?.('externalId:', true);
      if (externalId) {
        acc[curr?.blueprint?.attribute?.path] = externalId.split(':');
      }
      return acc;
    }, {});
    const parentAttributes = attributes.reduce((acc, curr) => {
      if (curr.valueType === 'parentRelationship') {
        acc[curr?.blueprint?.updatePath] = curr;
      }
      return acc;
    }, {});
    const nameAttr = Attributes.getAttribute('name', target?.type);
    const toSendDevices = newDevices.map(newDevice => {
      const toSend = {};
      const data = [];
      const parentRelationships = {};
      try {
        Object.entries(newDevice).forEach(([key, value]) => {
          if (!attrMap[key] || value === undefined) return;
          const parsedValue = ParseUtil.parseType(value, attrMap[key]);
          const attrPath = attrMap[key].blueprint.updatePath;
          if (validator?.fields[attrPath] && validator?.fields[attrPath].active) {
            const validatorValue = validator?.fields[attrPath].submitValidator(parsedValue);
            if (validatorValue === false || typeof validatorValue === 'string') {
              throw new Error(t('error', {
                value,
                key
              }));
            }
          }
          if (parentAttributes[attrPath]) {
            if (parsedValue) {
              parentRelationships[attrPath] = {
                value: parsedValue,
                attribute: parentAttributes[attrPath]
              };
            }
            return;
          }
          if (attrPath.startsWith('data/')) {
            data.push({
              path: attrPath.slice(5),
              value: parsedValue
            });
            const externalIdKeys = idAttrs[attrPath.slice(5)];
            if (externalIdKeys) {
              if (!toSend.networkAliases) {
                toSend.networkAliases = {};
              }
              if (!toSend.networkAliases[externalIdKeys[0]]) {
                toSend.networkAliases[externalIdKeys[0]] = {};
              }
              toSend.networkAliases[externalIdKeys[0]][externalIdKeys[1]] = parsedValue;
              toSend.networkId = externalIdKeys[0];
            }
            if (nameAttr?.displayName === attrMap[key]?.displayName) {
              toSend.name = parsedValue;
            }
          } else if (attrPath.startsWith('header/')) {
            const updateKey = attrPath.slice(7);
            toSend[updateKey] = parsedValue;
            if (updateKey?.startsWith?.('networkAliases')) {
              const [, networkId] = updateKey.split('/');
              toSend.networkId = networkId;
            }
          }
        });
      } catch (ex) {
        return ex;
      }
      if (data.length > 0) {
        toSend.data = data;
      }
      return {
        ...toSend,
        parentRelationships
      };
    });
    if (typeof onSubmit === 'function') {
      onSubmit({
        value: toSendDevices,
        data: eventData,
        original: evt
      });
    }
  };
  getAttributes() {
    const {
      getAttributes,
      target
    } = this.props;
    return this.cache.calc((target, getAttributes) => {
      const attrs = getAttributes(target, bulkCreateAttributeFilter) || [];

      // sort them
      attrs.sort(sort);
      return attrs;
    }, target, getAttributes);
  }
  buildValidator() {
    const attributes = this.getAttributes();
    const validators = {
      fields: {}
    };
    attributes.forEach(attr => {
      const validatorType = attr.blueprint?.attribute?.getTagStartingWith?.('formValidator:', true) || attr.name;
      const field = attr?.blueprint?.updatePath;
      const validator = BlueprintAttributeFormValidatorFactory.create(validatorType, attr);
      if (validator) {
        validators.fields[field] = validator;
      }
    });
    return validators;
  }
  onSubmitUpload = ({
    value
  }) => {
    this.setState({
      step: 'mapFields',
      data: value?.data?.trim(),
      fileName: value?.fileName
    });
  };
  onBackFromMapping = () => {
    this.setState({
      step: 'upload'
    });
  };
  onSubmitMapping = ({
    value: attrMap
  }) => {
    this.setState({
      attrMap
    }, () => this.onSubmit());
  };
  render() {
    const {
      submitText,
      ...rest
    } = this.props;
    const {
      step,
      data,
      fileName
    } = this.state;
    return step === 'upload' ? /*#__PURE__*/_jsx(BulkCreateUpload, {
      ...rest,
      onSubmit: this.onSubmitUpload,
      data: data,
      fileName: fileName
    }) : /*#__PURE__*/_jsx(BulkCreateMapFields, {
      ...this.props,
      onBack: this.onBackFromMapping,
      onSubmit: this.onSubmitMapping,
      data: data,
      fileName: fileName
    });
  }
}
export default connect((state, props) => {
  return {};
})(BulkCreateUploadForm);